import React, { useState } from 'react';
import moment from 'moment';
import cn from 'classnames';
import endpoints from '../../../define/endpoints';
import { TRANSLATIONS } from '../../../../translations';
import { formatDate, formatDateTime } from '../../../utils/format';
import BetaIcon, { IconSizes } from '../../Icons/Icon';
import Typography, {
  TypographyStates,
  TypographyVariants,
  TypographyWeights,
} from '../../../ui-components/Typography/Typography';
import { ORDER_TYPES } from '../../../define/constants';
import Tooltip from '../../Tooltip';
import { useListingCancellation } from '../../../hooks/useListingCancellation';
import { GIFTCARD_COVER_URL } from '../../../../constants';

const VOUCHER_OPTION = 'voucher_option';
//TODO: verify es path

const ModalOrderCard = ({
  order,
  compact,
  noBorder = false,
  toBeRefunded = false,
  toReschedule = false,
  toVoucher = false,
  toGiftcard = false,
  motivation = null,
  newDate = null,
  editRequest,
  showPolicy = false,
  priceDifference = null,
}) => {
  if (!order) return;

  const participants = order.items.filter(
    (option) => option.type !== VOUCHER_OPTION
  );
  const [labelCancellation, tooltipCancellation] = useListingCancellation(
    order && order.listing && order.listing.cancellation
  );
  const backgroundImageUrl =
    order.type === ORDER_TYPES.giftcard
      ? `url(${endpoints.bucket}${GIFTCARD_COVER_URL}?smart;width=90), url(${endpoints.bucket}images/listing-cover-placeholder.jpg?width=90)`
      : `url(${endpoints.bucket}${order.listing.coverImage}?smart;width=90), url(${endpoints.bucket}images/listing-cover-placeholder.jpg?width=90)`;

  if (compact) {
    return (
      <div className="d-flex gap-xs">
        {order.listing.coverImage && (
          <div
            className="modal-recal-card"
            style={{
              backgroundImage: backgroundImageUrl,
            }}
          ></div>
        )}
        <div className="d-flex flex-column gap-2xs">
          <>
            <Typography
              variant={TypographyVariants.p3}
              weight={TypographyWeights.bold}
              classNames="text-left"
            >
              {order.listing?.title}
            </Typography>
            <div className="d-flex gap-xl">
              <div>
                <Typography
                  variant={TypographyVariants.p4}
                  classNames="text-left"
                  weight={TypographyWeights.bold}
                >
                  {TRANSLATIONS.ORDER_ID}
                </Typography>
                <Typography
                  variant={TypographyVariants.p4}
                  classNames="text-left"
                >
                  {order.id}
                </Typography>
              </div>
              <div>
                <Typography
                  variant={TypographyVariants.p4}
                  classNames="text-left"
                  weight={TypographyWeights.bold}
                >
                  {order.type === ORDER_TYPES.voucher &&
                    TRANSLATIONS.VOUCHER_VALUE}
                  {order.type === ORDER_TYPES.giftcard && TRANSLATIONS.QUANTITY}
                </Typography>

                {order.type === ORDER_TYPES.voucher && (
                  <Typography
                    variant={TypographyVariants.p4}
                    classNames="text-left"
                  >
                    €{order.total}
                  </Typography>
                )}

                {order.type === ORDER_TYPES.giftcard && (
                  <Typography
                    variant={TypographyVariants.p4}
                    classNames="text-left"
                  >
                    {order.items[0]['quantity']} x{' '}
                    {order.items[0]['price'] / order.items[0]['quantity']}€
                  </Typography>
                )}
              </div>
            </div>
          </>
        </div>
      </div>
    );
  }

  if (toBeRefunded) {
    return (
      <div
        className={`d-flex flex-column gap-xs radius-xs p-xs ${noBorder ? '' : 'border-secondary-100'
          }`}
      >
        <div className="d-flex gap-xs">
          <div
            className="modal-recal-card"
            style={{
              backgroundImage: backgroundImageUrl,
            }}
          ></div>
          <div className="d-flex flex-column gap-2xs">
            <Typography
              variant={TypographyVariants.p3}
              weight={TypographyWeights.bold}
              classNames="text-left"
            >
              #{order.id} - {order.listing?.title}
            </Typography>
            <div>
              <div className="d-flex items-center gap-base">
                <>
                  <BetaIcon name={'calendar'} size={IconSizes[16]} />
                  <Typography
                    variant={TypographyVariants.p4}
                    classNames="text-left"
                  >
                    {formatDateTime(order.bookingDatetime)}
                  </Typography>
                </>
              </div>
            </div>
          </div>
        </div>

        <>
          <div>
            <Typography
              variant={TypographyVariants.p4}
              weight={TypographyWeights['semi-bold']}
              classNames="text-left"
            >
              {TRANSLATIONS.TO_BE_REFUNDED}
            </Typography>

            {order.items &&
              order.items.map((item, index) => (
                <div
                  key={index}
                  className="d-flex items-center justify-between"
                >
                  <Typography
                    variant={TypographyVariants.p4}
                    classNames="text-left"
                  >
                    {`${item.quantity} x ${item.label}`}
                  </Typography>
                  <Typography
                    variant={TypographyVariants.p4}
                    classNames="text-left"
                  >
                    {item.price}€
                  </Typography>
                </div>
              ))}
          </div>

          <div className="border-b-secondary-100"></div>
          <div className="d-flex items-center justify-between">
            <Typography
              variant={TypographyVariants.p3}
              weight={TypographyWeights['semi-bold']}
              classNames="text-left"
            >
              {TRANSLATIONS.TOTAL}
            </Typography>

            <Typography
              variant={TypographyVariants.p1}
              weight={TypographyWeights['semi-bold']}
              classNames="text-left"
            >
              {order.total}€
            </Typography>
          </div>

          {showPolicy && (
            <div className="d-flex items-center justify-between">
              <Typography
                variant={TypographyVariants.p3}
                weight={TypographyWeights['semi-bold']}
                classNames="text-left"
              >
                {TRANSLATIONS.REFUND_POLICY}
              </Typography>

              <div className="d-flex items-center">
                <Tooltip
                  classNames="freedome-tooltip--verified"
                  text={tooltipCancellation}
                  withMarginRight={true}
                />
                <Typography
                  variant={TypographyVariants.p3}
                  classNames="text-left"
                >
                  {labelCancellation}
                </Typography>
              </div>
            </div>
          )}

          {order.status !== 'completed-booking' && (
            <div className="d-flex items-center justify-between">
              <Typography
                variant={TypographyVariants.p3}
                weight={TypographyWeights['semi-bold']}
                classNames="text-left"
              >
                {TRANSLATIONS.TOTAL_TO_BE_REFUNDED}
              </Typography>

              <Typography
                variant={TypographyVariants.p2}
                weight={TypographyWeights['semi-bold']}
                classNames="text-left"
              >
                {order.totalRefundable}€
              </Typography>
            </div>
          )}
        </>
        {motivation && (
          <>
            <div className="border-b-secondary-100"></div>
            <div>
              <Typography
                variant={TypographyVariants.p3}
                weight={TypographyWeights['semi-bold']}
                classNames="text-left pb-3xs"
              >
                {TRANSLATIONS.MOTIVATION}
              </Typography>
              <Typography
                variant={TypographyVariants.p3}
                classNames="text-left"
              >
                {motivation}
              </Typography>
            </div>
          </>
        )}
      </div>
    );
  }

  if (toReschedule) {
    return (
      <div
        className={`d-flex flex-column gap-xs radius-xs p-xs ${noBorder ? '' : 'border-secondary-100'
          }`}
      >
        <div className="d-flex gap-xs">
          <div
            className="modal-recal-card"
            style={{
              backgroundImage: backgroundImageUrl,
            }}
          ></div>

          <div className="d-flex flex-column gap-2xs">
            <Typography
              variant={TypographyVariants.p3}
              weight={TypographyWeights.bold}
              classNames="text-left"
            >
              #{order.id} - {order.listing?.title}
            </Typography>
            <div>
              <div className="d-flex flex-column gap-2xs">
                <div className="d-flex items-center gap-base">
                  <BetaIcon name={'participants'} size={IconSizes[16]} />
                  <Typography
                    variant={TypographyVariants.p4}
                    classNames="text-left"
                  >
                    {participants
                      .map((item) => `${item.quantity} x ${item.label}`)
                      .join(', ')}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex flex-column">
          <Typography
            variant={TypographyVariants.p4}
            weight={TypographyWeights['semi-bold']}
            classNames="text-left pb-base"
          >
            {TRANSLATIONS.NEW_DATE_REQUESTED}
          </Typography>

          <div className="d-flex items-center justify-between">
            <div className="d-flex items-center gap-base">
              <BetaIcon name={'calendar'} size={IconSizes[16]} />
              <Typography
                variant={TypographyVariants.p4}
                classNames="text-left"
              >
                {newDate}
              </Typography>
            </div>

            {editRequest && (
              <button className="pointer" onClick={() => editRequest()}>
                <Typography
                  variant={TypographyVariants.p4}
                  state={TypographyStates.underlined}
                  classNames="text-left"
                >
                  {TRANSLATIONS.EDIT}
                </Typography>
              </button>
            )}
          </div>
        </div>

        {!!priceDifference && (
          <>
            <div className="border-b-secondary-100"></div>
            <div className="d-flex items-center justify-between">
              <Typography
                variant={TypographyVariants.p3}
                weight={TypographyWeights['semi-bold']}
                classNames="text-left"
              >
                {TRANSLATIONS.CHARGE} - {formatDate(order.createdAt)}
              </Typography>

              <Typography
                variant={TypographyVariants.p2}
                weight={TypographyWeights['normal']}
                classNames="text-left"
              >
                {order.total}€
              </Typography>
            </div>
            <div className="d-flex items-center justify-between">
              <Typography
                variant={TypographyVariants.p3}
                weight={TypographyWeights['semi-bold']}
                classNames="text-left"
              >
                {priceDifference < 0
                  ? TRANSLATIONS.REFUND
                  : TRANSLATIONS.ONSITE_PAYMENT}
              </Typography>

              <Typography
                variant={TypographyVariants.p2}
                weight={TypographyWeights['normal']}
                classNames="text-left"
              >
                {Math.abs(priceDifference)}€
              </Typography>
            </div>
            <div className="d-flex items-center justify-between">
              <Typography
                variant={TypographyVariants.p3}
                weight={TypographyWeights['semi-bold']}
                classNames="text-left"
              >
                {TRANSLATIONS.TOTAL}
              </Typography>

              <Typography
                variant={TypographyVariants.p2}
                weight={TypographyWeights['semi-bold']}
                classNames="text-left"
              >
                {order.total + priceDifference}€
              </Typography>
            </div>
          </>
        )}

        {motivation && (
          <>
            <div className="border-b-secondary-100"></div>
            <div>
              <Typography
                variant={TypographyVariants.p3}
                weight={TypographyWeights['semi-bold']}
                classNames="text-left pb-3xs"
              >
                {TRANSLATIONS.MOTIVATION}
              </Typography>
              <Typography
                variant={TypographyVariants.p3}
                classNames="text-left"
              >
                {motivation}
              </Typography>
            </div>
          </>
        )}
      </div>
    );
  }

  if (toGiftcard || toVoucher) {
    return (
      <div
        className={`d-flex flex-column gap-xs radius-xs p-xs ${noBorder ? '' : 'border-secondary-100'
          }`}
      >
        <div className="w-100% flex-row dialog dialog--feature">
          <BetaIcon
            name={toGiftcard ? 'gift' : 'ticket'}
            size={IconSizes[16]}
          />
          <p className="voucher-duration__text">
            {toGiftcard
              ? TRANSLATIONS.GIFT_CARD_VALID_18_MONTHS
              : app_main_wpml.voucher}
          </p>
        </div>

        <div className="d-flex gap-xs">
          <div
            className="modal-recal-card"
            style={{
              backgroundImage: backgroundImageUrl,
            }}
          ></div>

          <div className="d-flex flex-column gap-2xs">
            {toGiftcard ? (
              <Typography
                variant={TypographyVariants.p3}
                weight={TypographyWeights.bold}
                classNames="text-left"
              >
                #{order.id} - {app_main_wpml.gift_card_title}
              </Typography>
            ) : (
              <Typography
                variant={TypographyVariants.p3}
                weight={TypographyWeights.bold}
                classNames="text-left"
              >
                #{order.id} - {order.listing?.title}
              </Typography>
            )}
            <div>
              <div className="d-flex items-center gap-base">
                {toBeRefunded && (
                  <>
                    <BetaIcon name={'calendar'} size={IconSizes[16]} />
                    <Typography
                      variant={TypographyVariants.p4}
                      classNames="text-left"
                    >
                      {formatDateTime(order.bookingDatetime)}
                    </Typography>
                  </>
                )}
                <>
                  <BetaIcon name={'calendar'} size={IconSizes[16]} />
                  <Typography
                    variant={TypographyVariants.p4}
                    classNames="text-left"
                  >
                    {TRANSLATIONS.EXPIRES_ON}:{' '}
                    {toVoucher && formatDate(moment().add(1, 'year'))}
                    {toGiftcard && formatDate(moment().add(18, 'months'))}
                  </Typography>
                </>
              </div>
            </div>
          </div>
        </div>
        <>
          <div>
            <Typography
              variant={TypographyVariants.p4}
              weight={TypographyWeights['semi-bold']}
              classNames="text-left"
            >
              {toVoucher &&
                TRANSLATIONS.PARTICIPANTS.charAt(0).toUpperCase() +
                TRANSLATIONS.PARTICIPANTS.slice(1)}
              {toGiftcard && 'Quantità'}
            </Typography>

            {toVoucher &&
              order.items &&
              order.items.map((item, index) => (
                <div
                  key={index}
                  className="d-flex items-center justify-between"
                >
                  <Typography
                    variant={TypographyVariants.p4}
                    classNames="text-left"
                  >
                    {`${item.quantity} x ${item.label}`}
                  </Typography>
                  <Typography
                    variant={TypographyVariants.p4}
                    classNames="text-left"
                  >
                    {`${item.price}€`}
                  </Typography>
                </div>
              ))}
            {toGiftcard && (
              <div className="d-flex items-center justify-between">
                <Typography
                  variant={TypographyVariants.p4}
                  classNames="text-left"
                >
                  {`1 x ${order.total}€`}
                </Typography>
                <Typography
                  variant={TypographyVariants.p4}
                  classNames="text-left"
                >
                  {`${order.total}€`}
                </Typography>
              </div>
            )}
          </div>

          {showPolicy && (
            <div className="d-flex items-center justify-between">
              <Typography
                variant={TypographyVariants.p3}
                weight={TypographyWeights['semi-bold']}
                classNames="text-left"
              >
                {TRANSLATIONS.REFUND_POLICY}
              </Typography>

              <div className="d-flex items-center">
                <Tooltip
                  classNames="freedome-tooltip--verified"
                  text={tooltipCancellation}
                  withMarginRight={true}
                />
                <Typography
                  variant={TypographyVariants.p3}
                  classNames="text-left"
                >
                  {labelCancellation}
                </Typography>
              </div>
            </div>
          )}
          {order.status !== 'completed-booking' && (
            <>
              <div className="border-b-secondary-100"></div>
              <div className="d-flex items-center justify-between">
                <Typography
                  variant={TypographyVariants.p3}
                  weight={TypographyWeights['semi-bold']}
                  classNames="text-left"
                >
                  {TRANSLATIONS.TOTAL}
                </Typography>

                <Typography
                  variant={TypographyVariants.p1}
                  weight={TypographyWeights['semi-bold']}
                  classNames="text-left"
                >
                  {`${order.totalRefundable}€`}
                </Typography>
              </div>
            </>
          )}
        </>

        {motivation && (
          <>
            <div className="border-b-secondary-100"></div>
            <div>
              <Typography
                variant={TypographyVariants.p3}
                weight={TypographyWeights['semi-bold']}
                classNames="text-left pb-3xs"
              >
                {TRANSLATIONS.MOTIVATION}
              </Typography>
              <Typography
                variant={TypographyVariants.p3}
                classNames="text-left"
              >
                {motivation}
              </Typography>
            </div>
          </>
        )}
      </div>
    );
  }

  const recapCard = (
    <div
      className={`d-flex flex-column gap-xs radius-xs p-xs ${noBorder ? '' : 'border-secondary-100'
        }`}
    >
      <div className="d-flex gap-xs">
        <div
          className="modal-recal-card"
          style={{
            backgroundImage: backgroundImageUrl,
          }}
        ></div>
        <div className="d-flex flex-column gap-2xs">
          <Typography
            variant={TypographyVariants.p3}
            weight={TypographyWeights.bold}
            classNames="text-left"
          >
            #{order.id} - {order.listing?.title}
          </Typography>
          <div>
            <div className="d-flex flex-column gap-2xs">
              <div className="d-flex items-center gap-base">
                <BetaIcon name={'calendar'} size={IconSizes[16]} />
                <Typography
                  variant={TypographyVariants.p4}
                  classNames="text-left"
                >
                  {formatDateTime(order.bookingDatetime)}
                </Typography>
              </div>

              <div className="d-flex items-center gap-base">
                <BetaIcon name={'participants'} size={IconSizes[16]} />
                <Typography
                  variant={TypographyVariants.p4}
                  classNames="text-left"
                >
                  {participants
                    .map((item) => `${item.quantity} x ${item.label}`)
                    .join(', ')}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </div>
      {motivation && (
        <>
          <div className="border-b-secondary-100"></div>
          <div>
            <Typography
              variant={TypographyVariants.p3}
              weight={TypographyWeights['semi-bold']}
              classNames="text-left pb-3xs"
            >
              {TRANSLATIONS.MOTIVATION}
            </Typography>
            <Typography variant={TypographyVariants.p3} classNames="text-left">
              {motivation}
            </Typography>
          </div>
        </>
      )}
    </div>
  );

  return recapCard;
};

ModalOrderCard.propTypes = {};

export { ModalOrderCard };
